import { message } from 'antd';
import { TFunction } from 'i18next';

import {
    FileAccessResponse, OfficeViewerResponse, PdfViewerResponse, SecuredFile,
} from '../../types/types';
import { ErrorTypes, IframeParams } from './interfaces';
import {
    getErrorResponse,
    checkMessageContainsMFA,
    checkMessageContainsMFAPassword,
    checkMessageContainsPermissions,
} from '../utils';
import {
    DOWNLOAD_ERRORS_NAME_SPACE,
    DOWNLOAD_SPECIFIC_ERRORS,
    ERRORS_NAME_SPACE,
    DOWNLOAD_ERROR_MESSAGE_DURATION,
} from './constants';

export const getErrorType = (error: unknown): ErrorTypes => {
    const { statusCode, message: errorMessage } = getErrorResponse(error);
    let errorType: ErrorTypes;
    const isMFAError = checkMessageContainsMFA(errorMessage);
    const isMFAPasswordError = checkMessageContainsMFAPassword(errorMessage);
    const isPermissionsError = checkMessageContainsPermissions(errorMessage);
    switch (statusCode) {
    case 404:
    case 500:
        errorType = statusCode;
        break;
    case 401:
        if (isMFAPasswordError) {
            errorType = 'mfaPasswordRequired';
        } else if (isMFAError) {
            errorType = 'mfaRequired';
        } else {
            errorType = 401;
        }
        break;
    case 403:
        if (isMFAError) {
            errorType = 'mfaFail';
        } else if (isPermissionsError) {
            errorType = 'noPermissions';
        } else {
            errorType = 403;
        }
        break;
    case 400:
    case 406:
        errorType = 'optionNotSupported';
        break;
    case 408:
        errorType = 'fileBeingPrepared';
        break;
    default:
        errorType = 500;
    }
    return errorType;
};

export const getErrorMessage = (errorType: ErrorTypes, t: TFunction): string => {
    if (errorType === 404) {
        return t('general.errors.404', { type: '$t(general.specterxCommon.file)' });
    }
    if (errorType === 'mfaPasswordRequired') {
        // Re-use generic MFA-required message for password-based MFA in the viewer.
        return t('viewer.error.mfaRequired');
    }
    return t(`viewer.error.${errorType}`);
};

export const showDownloadErrorMessage = (errorType: ErrorTypes, t: TFunction): void => {
    const messageNameSpace: string = DOWNLOAD_SPECIFIC_ERRORS.includes(errorType)
        ? DOWNLOAD_ERRORS_NAME_SPACE
        : ERRORS_NAME_SPACE;
    message.error(t(`${messageNameSpace}.${errorType}`), DOWNLOAD_ERROR_MESSAGE_DURATION);
};

export const checkEditPermissions = (
    currentUserEmail: string,
    file: SecuredFile,
): boolean => {
    if (currentUserEmail === file?.owner_email) {
        return true;
    }
    return file?.menu?.read_only === false;
};

export const hasViewerSession = (params: IframeParams | undefined | null): boolean => !!(
    (params as OfficeViewerResponse)?.viewUrl
    || (params as FileAccessResponse)?.link
    || (params as PdfViewerResponse)?.pdfViewer
);
